import { hydraUiApi } from '@/services/api';
import type { RolesRoleDefinitionsResponse } from '@customerio/hydra-ui-api';
import { deleteCrossAppCacheKeys, getCrossAppCacheAsync, setCrossAppCache } from '@/utils/cross-app-cache';

/**
 * How long to wait on a request another app bundle cached under this key.
 * Joining it saves a duplicate fetch, but that promise belongs to the bundle
 * that made it: this app cannot abort it and cannot make it settle. Waiting on
 * it without a bound holds every reader for as long as it stays open, and the
 * authenticated layout holds the whole tree while role definitions are pending.
 * Well under the 10s entry TTL, so the fallback request still populates the key
 * for later readers.
 */
const CACHE_JOIN_TIMEOUT_MS = 2000;

/**
 * Reads the cache entry, treating a slow or failed read as a miss so the caller
 * fetches for itself and reports its own outcome.
 */
async function readCachedRoleDefinitions(url: string): Promise<RolesRoleDefinitionsResponse | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;

  const timeout = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), CACHE_JOIN_TIMEOUT_MS);
  });

  // A failure belonging to another bundle's request says nothing about this one,
  // and swallowing it here also keeps the losing side of the race from raising
  // an unhandled rejection after the timeout.
  const cacheRead = getCrossAppCacheAsync(url).catch(() => null);

  try {
    const cachedRoleDefinitions = await Promise.race([cacheRead, timeout]);
    if (cachedRoleDefinitions == null) {
      return null;
    }

    // The cross-app cache is untyped; the entry is a role-definitions response.
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    return cachedRoleDefinitions as RolesRoleDefinitionsResponse;
  } finally {
    clearTimeout(timer);
  }
}

export const getRoleDefinitions = async (accountId: number): Promise<RolesRoleDefinitionsResponse> => {
  const url = `/accounts/${accountId}/role_definitions`;
  const cachedRoleDefinitions = await readCachedRoleDefinitions(url);
  if (cachedRoleDefinitions != null) {
    return cachedRoleDefinitions;
  }
  const request = hydraUiApi.roles.getRoleDefinitions({ params: { path: { account_id: String(accountId) } } });
  // Cache the request itself, so if we catch it halfway through, we can await for it instead of making a new request.
  setCrossAppCache({ key: url, value: request, ttl: 10000, isAsync: true });
  try {
    const data = await request;
    // Once it's complete, we can replace the request with the data.
    setCrossAppCache({ key: url, value: data, ttl: 10000 });
    return data;
  } catch (error) {
    // Drop the rejected request so the next reader starts a new one, and let the
    // failure reach the caller. A swallowed error resolves to the same value as a
    // fetch that has not landed yet, so no caller can tell the two apart.
    deleteCrossAppCacheKeys(url);
    throw error;
  }
};
